import {
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ArrowDown, ArrowLeft, ArrowUp } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Page } from "../../../../components/Page";
import { H1 } from "../../../../components/text/headings";
import LocalSpinner from "../../../../components/LocalSpinner";
import { routes } from "../../../../config";
import { getFormattedNumValue } from "../../../../utils";
import { useGetSupplierEngagementReport } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { useGetSupplierQuestionnaireById } from "../../../../hooks/query/supplier-questionnaire/supplier-questionnaire";
import TargetBar from "./TargetBar";

const formatTco2 = (v) => {
  if (v == null || isNaN(Number(v))) return "—";
  return getFormattedNumValue(Number(v), 2, true);
};

const formatQty = (v, decimals = 2) => {
  if (v == null || isNaN(Number(v))) return "—";
  return getFormattedNumValue(Number(v), decimals, true);
};

// KPI card matching ScopeTwoDetail style: 3px accent bar, bold title + subtitle,
// clamp font for the number, pill YoY chip flush beside the number.
const KpiCard = ({ accent, title, subtitle, value, previousValue, unit, loading, children }) => {
  const hasCurrent = typeof value === "number";
  const hasPrev = typeof previousValue === "number" && previousValue !== 0;
  const pctChange = hasCurrent && hasPrev ? ((value - previousValue) / previousValue) * 100 : null;
  const isImprovement = pctChange == null ? null : pctChange < 0;
  const pillBg = isImprovement === null ? "gray.100" : isImprovement ? "green.50" : "red.50";
  const pillColor = isImprovement === null ? "gray.500" : isImprovement ? "green.700" : "red.600";
  const Arrow = pctChange == null ? null : pctChange < 0 ? ArrowDown : ArrowUp;

  return (
    <Box position="relative" pt={3} sx={{ "--big": "clamp(20px, 3vw, 56px)" }}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        {subtitle && <Text fontSize="13px" color="gray.500">{subtitle}</Text>}
      </HStack>
      {loading ? (
        <Text fontSize="var(--big)" fontWeight="700" color="darkTeal" lineHeight="1">…</Text>
      ) : children ? (
        children
      ) : (
        <HStack align="stretch" spacing={3} flexWrap="nowrap">
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color="darkTeal"
            lineHeight="1"
            letterSpacing="-0.02em"
            whiteSpace="nowrap"
          >
            {hasCurrent ? formatTco2(value) : "—"}
          </Text>
          <Flex direction="column" justify="space-between" py={0.5} flexShrink={0}>
            {pctChange != null ? (
              <HStack
                spacing={1}
                bg={pillBg}
                color={pillColor}
                px={2}
                py={0.5}
                borderRadius="full"
                alignSelf="flex-start"
              >
                {Arrow && <Arrow size={11} weight="bold" />}
                <Text fontSize="11px" fontWeight="600">
                  {Math.abs(pctChange).toFixed(1)} %
                </Text>
              </HStack>
            ) : (
              <Box />
            )}
            <Text fontSize="calc(var(--big) * 0.32)" fontWeight="600" color="gray.600" lineHeight="1">
              {unit || "tCO₂e"}
            </Text>
          </Flex>
        </HStack>
      )}
    </Box>
  );
};

const SupplierProductDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get("supplierId");
  const questionnaireId = searchParams.get("questionnaireId");
  const year = Number(searchParams.get("year")) || new Date().getFullYear();

  const [currentData, setCurrentData] = useState(null);
  const [previousData, setPreviousData] = useState(null);

  const { isFetching: engagementLoading } = useGetSupplierEngagementReport(
    [year, year - 1],
    supplierId,
    {
      enabled: !!supplierId && !!year,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: (data) => {
        const toAttrib = (d, y) => {
          if (!d) return null;
          const productAttributed = d?.engagementReport?.productCo2Emissions || 0;
          const locationAttributed = d?.engagementReport?.locationCo2Emissions || 0;
          const s1 = d?.engagementReport?.revenue?.scope1ApportionedCo2Emissions || 0;
          const s2 = d?.engagementReport?.revenue?.scope2ApportionedCo2Emissions || 0;
          const s3 = d?.engagementReport?.revenue?.scope3ApportionedCo2Emissions || 0;
          const totalAttributed = s1 + s2 + s3 + productAttributed + locationAttributed;
          const target = d?.yearlyEmission?.yearlyEmissionData?.[y]?.target || 0;
          const delta = target ? ((totalAttributed - target) / target) * 100 : 0;
          return {
            totalAttributed,
            target,
            delta,
            supplierName: d?.yearlyEmission?.supplierName,
          };
        };
        setCurrentData(toAttrib(data?.[year], year));
        setPreviousData(toAttrib(data?.[year - 1], year - 1));
      },
    }
  );

  const { data: questionnaireData, isFetching: questLoading } = useGetSupplierQuestionnaireById(
    questionnaireId,
    { enabled: !!questionnaireId, cacheTime: 0, staleTime: 0 }
  );

  const products = useMemo(() => questionnaireData?.products || [], [questionnaireData]);

  const grandTotal = useMemo(
    () =>
      products.reduce((sum, p) => {
        const qty = Number(p.quantity) || 0;
        const factor = Number(p.totalCo2Emissions) || 0;
        const rowTotal = p.unit === "KG" ? (qty / 1000) * factor : qty * factor;
        return sum + rowTotal;
      }, 0),
    [products]
  );

  const hasDelta =
    typeof currentData?.delta === "number" &&
    typeof currentData?.target === "number" &&
    currentData.target !== 0;
  const overTarget = hasDelta && currentData.delta > 0;

  const backUrl = `${routes.supplierInsightsIndividualDetail}?supplierId=${supplierId}`;

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(backUrl)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Back</Text>
        </Flex>

        <H1>{currentData?.supplierName || "Loading"} — Products</H1>
        <Text maxW="75%" color="gray.500" fontSize="body.m">
          Per-product emissions breakdown from the supplier&apos;s product-level questionnaire.
        </Text>

        {/* KPI row — ScopeTwoDetail style */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mt={8} mb={8}>
          <KpiCard
            accent="pureBlack"
            title="Total Attributed"
            subtitle="tCO₂e"
            value={currentData?.totalAttributed}
            previousValue={previousData?.totalAttributed}
            loading={engagementLoading}
          />

          <KpiCard
            accent="lime"
            title="Products"
            subtitle="reported"
            loading={questLoading}
          >
            <HStack align="stretch" spacing={3} flexWrap="nowrap" sx={{ "--big": "clamp(20px, 3vw, 56px)" }}>
              <Text
                fontSize="var(--big)"
                fontWeight="700"
                color="darkTeal"
                lineHeight="1"
                letterSpacing="-0.02em"
                whiteSpace="nowrap"
              >
                {products.length}
              </Text>
              <Flex direction="column" justify="flex-end" py={0.5} flexShrink={0}>
                <Text fontSize="calc(var(--big) * 0.32)" fontWeight="600" color="gray.600" lineHeight="1">
                  product{products.length !== 1 ? "s" : ""}
                </Text>
              </Flex>
            </HStack>
          </KpiCard>

          <KpiCard
            accent={!hasDelta ? "gray.300" : overTarget ? "red.400" : "green.400"}
            title="Target Delta"
            subtitle={!hasDelta ? "vs target" : overTarget ? "over target" : "under target"}
            loading={engagementLoading}
          >
            <Box sx={{ "--big": "clamp(20px, 3vw, 56px)" }}>
              <Text
                fontSize="var(--big)"
                fontWeight="700"
                color={!hasDelta ? "darkTeal" : overTarget ? "red.600" : "green.600"}
                lineHeight="1"
                letterSpacing="-0.02em"
                whiteSpace="nowrap"
                mb={2}
              >
                {hasDelta
                  ? `${overTarget ? "+" : ""}${currentData.delta.toLocaleString("en-GB", { maximumFractionDigits: 1 })} %`
                  : "—"}
              </Text>
              <TargetBar delta={hasDelta ? currentData.delta : null} />
              <HStack spacing={1} mt={3} align="baseline" flexWrap="wrap">
                <Text fontSize="13px" color="gray.500">Actual</Text>
                <Text fontSize="16px" fontWeight="700" color="darkTeal" lineHeight="1">
                  {formatTco2(currentData?.totalAttributed)}
                </Text>
                <Text fontSize="13px" color="gray.500">vs target</Text>
                <Text fontSize="16px" fontWeight="700" color="darkTeal" lineHeight="1">
                  {formatTco2(currentData?.target)}
                </Text>
                <Text fontSize="13px" color="gray.500">tCO₂e</Text>
              </HStack>
            </Box>
          </KpiCard>
        </SimpleGrid>

        {/* Product table */}
        <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>
          Products
        </Text>

        {questLoading ? (
          <Box p={6}>
            <LocalSpinner />
          </Box>
        ) : products.length === 0 ? (
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" bg="white" p={8}>
            <Text textAlign="center" color="gray.500" fontSize="13px">
              No products found for this questionnaire.
            </Text>
          </Box>
        ) : (
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white" mb={10}>
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="gray.50">
                  <Th
                    py={3}
                    px={4}
                    fontSize="11px"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    fontWeight="600"
                    textAlign="left"
                  >
                    Product
                  </Th>
                  <Th
                    py={3}
                    px={4}
                    fontSize="11px"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    fontWeight="600"
                    textAlign="right"
                  >
                    Quantity
                  </Th>
                  <Th
                    py={3}
                    px={4}
                    fontSize="11px"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    fontWeight="600"
                    textAlign="left"
                  >
                    Unit
                  </Th>
                  <Th
                    py={3}
                    px={4}
                    fontSize="11px"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    fontWeight="600"
                    textAlign="right"
                  >
                    tCO₂e / unit
                  </Th>
                  <Th
                    py={3}
                    px={4}
                    fontSize="11px"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    fontWeight="600"
                    textAlign="right"
                  >
                    Total tCO₂e
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {products.map((p, idx) => {
                  const qty = Number(p.quantity) || 0;
                  // p.totalCo2Emissions is the emission factor (tCO₂e per unit),
                  // not the row total. Backend calculates actual total as:
                  //   KG:     (quantity / 1000) × factor
                  //   TONNES: quantity × factor
                  const factor = Number(p.totalCo2Emissions) || 0;
                  const rowTotal =
                    p.unit === "KG"
                      ? (qty / 1000) * factor
                      : qty * factor;
                  return (
                    <Tr
                      key={p.id ?? idx}
                      borderTop={idx === 0 ? "none" : "1px solid"}
                      borderColor="gray.100"
                    >
                      <Td py={4} pl={4} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                        {p.description || "—"}
                      </Td>
                      <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                        {formatQty(qty, 2)}
                      </Td>
                      <Td py={4} px={4} fontSize="14px" color="gray.500" textAlign="left">
                        {p.unit || "—"}
                      </Td>
                      <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                        {formatQty(factor, 4)}
                      </Td>
                      <Td py={4} px={4} fontSize="14px" color="limeShade.700" fontWeight="600" textAlign="right">
                        {formatTco2(rowTotal)}
                      </Td>
                    </Tr>
                  );
                })}
                <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                  <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="left" colSpan={4}>
                    TOTAL
                  </Td>
                  <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">
                    {formatTco2(grandTotal)}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        )}
      </Flex>
    </Page>
  );
};

export default SupplierProductDetail;
